import {
	DateObject,
	type FetchFanTokenOfferingsParams,
	minutesToMs,
	SociosApi,
	useSettings,
	voucherApi
} from '@capsicum/common';
import { useQuery } from '@tanstack/react-query';
import qs from 'qs';

import {
	type FanTokenOfferingWithPartner,
	type FetchFanTokenOfferingsDataWithPartner
} from '../../models/fanTokenOferringWithPartner';

/**
 * Call query to fetch Fan Tokens Offerings list
 *
 * @param {string} fanTokenCurrencyId: fan token currency id
 *
 * @returns FetchPaymentFanTokenRemainingLimitsData
 */

export const useFanTokenOfferingQuery = (
	params: FetchFanTokenOfferingsParams,
	select: (data: FetchFanTokenOfferingsDataWithPartner) => Array<FanTokenOfferingWithPartner>
) => {
	const {
		settings: { ftoAmountToShow }
	} = useSettings();

	return useQuery<FetchFanTokenOfferingsDataWithPartner, unknown, Array<FanTokenOfferingWithPartner>>({
		enabled: !!params['convertAmountToCurrencies[]']?.length,
		queryFn: async () =>
			(await voucherApi).fetchFanTokenOfferings(
				{
					'claimableEndDate[from]': new DateObject()
						.utc()
						.subtract(24, 'hours')
						.format('YYYY-MM-DDTHH:mm:ss+00:00'),
					direction: 'desc',
					limit: ftoAmountToShow,
					orderBy: 'startDate',
					'statuses[]': [
						'opened',
						'published',
						'closed',
						'locked',
						'finished',
						'airdropProcessing',
						'claimablePeriodOngoing',
						'claimablePeriodClosed'
					],
					...params
				},
				{
					headers: SociosApi.prepareEDTOHeaders([
						{
							at: '/data/*/voteToken/partnerId',
							details: ['partnerImages'],
							fetch: 'AclService:Partner:byId',
							into: 'partner'
						}
					]),
					paramsSerializer: params => qs.stringify(params, { arrayFormat: 'repeat', format: 'RFC3986' })
				}
			) as Promise<FetchFanTokenOfferingsDataWithPartner>,
		queryKey: [params, ftoAmountToShow],
		refetchOnWindowFocus: false,
		select,
		staleTime: minutesToMs(10)
	});
};
